import React from 'react';
import clsx from 'clsx';

import Icon from '../Icon';
import Link from '../NavLink';
import { tv } from '@utilities';
import { withAccess } from '../withAccess';
import OverlayTooltip from '../OverlayTooltip';

export type ButtonStyle =
  | 'primary'
  | 'light'
  | 'danger'
  | 'empty'
  | 'main'
  | 'ghost';
export type ButtonVariant = 'base' | 'icon';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type ButtonWidth = 'tiny' | 'small' | 'medium' | 'big' | 'full';

export type ButtonProps = AccessProps & {
  icon?: IconType;
  tooltip?: string;

  url?: string;
  loading?: boolean;
  disabled?: boolean;
  type?: ButtonStyle;
  size?: ButtonSize;
  variant?: ButtonVariant;
  fullWidth?: boolean;
  width?: ButtonWidth;
  label?: React.ReactNode;
  children?: React.ReactNode;

  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: React.MouseEventHandler<HTMLAnchorElement> &
    React.MouseEventHandler<HTMLButtonElement>;

  round?: boolean;
  rounded?: boolean;
  className?: string;
  textSizeClassName?: string;
  justify?: 'start' | 'center';
};

const buttonVariants = tv({
  base: 'inline-flex items-center font-medium transition-default box-border',
  variants: {
    variant: {
      base: 'gap-x-sm',
      icon: 'shrink-0 justify-center border p-0',
    },
    type: {
      danger: 'bg-[#ef4444] text-white border-[#ef4444] shadow-md opacity-80 ',
      light: 'bg-content text-text border-active shadow-md',
      primary: 'bg-active text-text border-active shadow-md',
      main: 'bg-main text-white border-main shadow-md dark:bg-white dark:text-main dark:border-main-dark',
      empty: 'border-transparent shadow-md',
      ghost: 'bg-transparent border-transparent text-grey',
    },
    size: {
      xs: 'text-tiny',
      sm: 'text-small',
      md: 'text-small',
      lg: 'text-small',
    },
    fullWidth: {
      true: 'w-full',
      false: '',
    },
    round: {
      true: 'rounded-full',
      false: '',
    },
    rounded: {
      true: 'rounded-sm',
      false: '',
    },
    disabled: {
      true: 'cursor-not-allowed opacity-40',
      false: '',
    },
    loading: {
      true: 'pointer-events-none cursor-wait relative',
      false: '',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
    },
    hasContent: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // we can't block pointer events as we can have tooltips for disabled buttons
    { disabled: false, type: 'danger', class: 'hover:opacity-100' },
    { disabled: false, type: 'light', class: ' hover:bg-active' },
    { disabled: false, type: 'main', class: 'hover:opacity-90' },
    { disabled: false, type: 'empty', class: 'hover:border-current' },
    { disabled: false, type: 'ghost', class: 'hover:bg-light' },

    { variant: 'base', round: false, class: 'border py-sm' },
    { variant: 'base', round: false, hasContent: false, class: 'px-md' },
    {
      variant: 'base',
      round: false,
      hasContent: true,
      size: 'xs',
      class: 'h-[3.2rem] px-md',
    },
    {
      variant: 'base',
      round: false,
      hasContent: true,
      size: 'sm',
      class: 'h-[3.6rem] px-lg',
    },
    {
      variant: 'base',
      round: false,
      hasContent: true,
      size: 'md',
      class: 'h-[4rem] px-lg',
    },
    {
      variant: 'base',
      round: false,
      hasContent: true,
      size: 'lg',
      class: 'h-[4.4rem] px-lg',
    },
    { variant: 'base', round: true, size: 'xs', class: 'size-[3.2rem]' },
    { variant: 'base', round: true, size: 'sm', class: 'size-[3.6rem]' },
    { variant: 'base', round: true, size: 'md', class: 'size-[4rem]' },
    { variant: 'base', round: true, size: 'lg', class: 'size-[4.4rem]' },
    { variant: 'icon', round: false, class: 'rounded-xs' },
    { variant: 'icon', round: true, class: 'rounded-full' },
    { variant: 'icon', size: 'xs', class: 'size-[1.6rem] text-tiny' },
    { variant: 'icon', size: 'sm', class: 'size-[2.4rem] text-tiny' },
    { variant: 'icon', size: 'md', class: 'size-[2.8rem] text-tiny' },
    { variant: 'icon', size: 'lg', class: 'size-[3.2rem] text-small' },
  ],
  defaultVariants: {
    variant: 'base',
    type: 'light',
    size: 'sm',
    fullWidth: false,
    round: false,
    rounded: true,
    disabled: false,
    loading: false,
    justify: 'center',
    hasContent: true,
  },
});

const widthToSize: Record<Exclude<ButtonWidth, 'full'>, ButtonSize> = {
  tiny: 'xs',
  small: 'sm',
  medium: 'md',
  big: 'lg',
};

const resolveSize = (
  size: ButtonSize | undefined,
  width: ButtonWidth | undefined,
  isIconVariant: boolean
): ButtonSize => {
  if (size) return size;
  if (width && width !== 'full') return widthToSize[width];

  return isIconVariant ? 'md' : 'md';
};

const ButtonComponent: React.FC<ButtonProps> = ({
  icon,
  type = 'light',
  variant = 'base',
  size: initialSize,
  fullWidth: initialFullWidth = false,
  width = 'small',
  justify = 'center',

  url,
  tooltip,
  label,
  children,
  loading,
  className,
  round = false,
  rounded = true,
  disabled: initialDisabled,

  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  const disabled = initialDisabled || loading;
  const isIconVariant = variant === 'icon';
  // const content = isIconVariant ? null : (children ?? label);
  const content = children ?? label;
  const hasContent = Boolean(content || icon);
  const size = resolveSize(initialSize, width, isIconVariant);
  const fullWidth = initialFullWidth || width === 'full';
  const isRound = round;

  const ElementType = url && !disabled ? Link : 'button';
  const element = (
    <ElementType
      to={url ? url : (undefined as any as string)}
      disabled={url ? disabled : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={!disabled ? onClick : undefined}
      className={buttonVariants({
        variant,
        type,
        size,
        fullWidth,
        round: isRound,
        rounded: isIconVariant ? false : isRound ? false : rounded,
        disabled: initialDisabled,
        loading,
        justify: isIconVariant ? 'center' : justify,
        hasContent,
        class: className,
      })}
    >
      <span
        className={clsx(
          'inline-flex items-center justify-center gap-x-sm',
          loading && 'invisible'
        )}
      >
        {Boolean(icon) && (
          <Icon
            width
            icon={icon}
            className={label ? 'text-left' : 'text-center'}
          />
        )}
        {content}
      </span>
      {/* this way we hide both icon and content and show loading as overlay with no width change */}
      {loading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Icon width icon="loading" />
        </span>
      )}
    </ElementType>
  );

  return tooltip ? (
    <OverlayTooltip tooltip={tooltip}>{element}</OverlayTooltip>
  ) : (
    element
  );
};

export default withAccess(ButtonComponent);
